import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, HelpCircle, LifeBuoy } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SupportPanel } from '@/components/support/SupportPanel';
import { SUPPORT_DOCS_URL } from '@/lib/support';

/**
 * Discreet, monochrome "Precisa de ajuda?" entry (F049; F161 replaces the
 * mailto placeholder with the in-app ticket panel).
 *
 * Resend-style help affordance mounted in the AppLayout header, so it is
 * reachable from every authenticated page. "Falar com o suporte" opens
 * `SupportPanel` (a right-side Sheet) instead of a `mailto:` link.
 */
export function SupportMenu() {
  const { t } = useTranslation();
  const [panelOpen, setPanelOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 hover:bg-muted"
            aria-label={t('support.aria_label')}
          >
            <HelpCircle className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('support.title')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a href={SUPPORT_DOCS_URL} target="_blank" rel="noreferrer" className="cursor-pointer">
              <BookOpen className="h-4 w-4 mr-2" />
              {t('support.docs')}
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="support-open-panel"
            className="cursor-pointer"
            onSelect={(event) => {
              // Radix returns focus to the (closing) trigger by default on
              // select, which races the Sheet's own focus trap as it opens.
              // Prevent that default; the Sheet takes over focus once it
              // mounts, after the dropdown has fully closed.
              event.preventDefault();
              setPanelOpen(true);
            }}
          >
            <LifeBuoy className="h-4 w-4 mr-2" />
            {t('support.contact')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SupportPanel open={panelOpen} onOpenChange={setPanelOpen} />
    </>
  );
}
